import { useMemo } from 'react';
import { FilterChip, TFilterConfig } from '@fe-learner-common';
import { useIntl } from 'react-intl';
import { toLocaleDate } from '@fe-learner-common/utils/date';
import { DateTime } from 'luxon';

export const useFilterChips = (filterConfig?: TFilterConfig | null): FilterChip[] => {
  const intl = useIntl();

  return useMemo(() => {
    if (!filterConfig) return [];
    const searchChip: FilterChip[] =
      filterConfig.search && filterConfig.search.value
        ? [
            {
              value: filterConfig.search.value,
              label: filterConfig.search.value,
              group: {
                label: intl.formatMessage({ id: 'learner.common.search' }),
                key: 'search',
              },
            },
          ]
        : [];

    const optionChips = filterConfig?.options?.reduce(
      (acc: FilterChip[], option) => {
        if (option.type === 'singleChoice') {
          const selectedItem = option.items.find((item) => item.key === option.selected);
          const obj: FilterChip = {
            group: {
              label: option.label,
              key: option.filterKey,
            },
            value: option.selected,
            label: selectedItem?.label ?? '',
          };
          return obj.value ? [...acc, obj] : acc;
        }

        if (option.type === 'multipleChoice') {
          const filterValues = Array.from(option.selectedItems);
          const mapped = filterValues.reduce((_acc, _cur) => {
            const selectedItem = option.items.find((item) => item.key === _cur);
            const obj: FilterChip = {
              group: {
                label: option.label,
                key: option.filterKey,
              },
              label: selectedItem?.label ?? '',
              value: _cur,
            };
            return [..._acc, obj];
          }, []);
          return mapped && mapped.length ? [...acc, ...mapped] : acc;
        }

        if (option.type === 'durationChoice') {
          if (!option.selectedItems?.min && !option.selectedItems?.max) {
            return acc;
          }

          const value = `${option.selectedItems.min}-${option.selectedItems.max}`;
          const obj: FilterChip = {
            group: {
              label: option.label,
              key: option.filterKey,
            },
            label: value,
            value,
          };
          return [...acc, obj];
        }

        if (option.type === 'dateRangeChoice') {
          if (!option.selectedItems?.start && !option.selectedItems?.end) {
            return acc;
          }

          const rangeStart = option.selectedItems.start &&
            toLocaleDate(option.selectedItems.start, DateTime.DATE_FULL);
          const rangeEnd = option.selectedItems.end &&
            toLocaleDate(option.selectedItems.end, DateTime.DATE_FULL);
          const value = `${rangeStart} - ${rangeEnd}`;
          const obj: FilterChip = {
            group: {
              label: option.label,
              key: option.filterKey,
            },
            label: value,
            value,
          };
          return [...acc, obj];
        }
      },

      [],
    );

    return [...searchChip, ...(optionChips || [])];
  }, [filterConfig, intl]);
};
